import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { PieChart } from "@mui/x-charts";

export interface LeaguePieArcProps {
  title?: string;
  correct?: number; // number of correct picks (or Wins when relabeled)
  incorrect?: number; // number of incorrect picks (or Losses when relabeled)
  push?: number; // number of pushes (ties)
  height?: number;
  half?: boolean; // render as a half pie (semi-donut)
  showPush?: boolean; // include Push segment when true
  showLegend?: boolean; // show legend under the chart
  minWidth?: number; // container min width
  innerRadius?: number; // thickness control
  correctLabel?: string;
  incorrectLabel?: string;
  pushLabel?: string;
  showBottomPercent?: boolean; // show bottom-center % chips for decided games
  percentYOffset?: number; // for half pies, fraction of height to place label (defaults ~0.62)
}

const LeaguePieArc: React.FC<LeaguePieArcProps> = ({
  title = "Picks Breakdown",
  correct,
  incorrect,
  push,
  height = 180,
  half = false,
  showPush = true,
  showLegend = true,
  minWidth = 240,
  innerRadius = 30,
  correctLabel = "Correct",
  incorrectLabel = "Incorrect",
  pushLabel = "Push",
  showBottomPercent = false,
  percentYOffset = 0.62,
}) => {
  const theme = useTheme();

  const c = correct ?? 0;
  const i = incorrect ?? 0;
  const p = showPush ? (push ?? 0) : 0;
  const total = c + i + (showPush ? p : 0);
  const decided = c + i; // exclude pushes for percent

  const hasData = total > 0;

  const showTitle = !!(title && title.trim().length > 0);

  // Value formatter for arcs; tailor tooltip values.
  const valueFormatter = (item: any) => {
    if (!hasData) return "";
    // id 0 corresponds to the first data entry (Correct)
    if (item.id === 0) return `${item.value}`;
    return `${item.value}`;
  };

  return (
    <Box sx={{ width: '100%', minWidth: { xs: 'unset', sm: minWidth } }}>
      {showTitle && (
        <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
          {title}
        </Typography>
      )}
      {!hasData ? (
        <Typography variant="body2" color="text.secondary" data-testid="pie-empty">
          No picks yet
        </Typography>
      ) : (
        <Box sx={{ width: '100%', position: 'relative' }}>
          <PieChart
            height={height}
            series={[
              {
                data: [
                  { id: 0, value: c, label: correctLabel, color: theme.palette.success.main },
                  { id: 1, value: i, label: incorrectLabel, color: theme.palette.error.main },
                  ...(showPush ? [{ id: 2, value: p, label: pushLabel, color: theme.palette.warning.main }] : []),
                ],
                innerRadius,
                paddingAngle: 2,
                ...(half ? { startAngle: -90, endAngle: 90 } : {}),
                valueFormatter,
              },
            ]}
            margin={{ left: 10, right: 10, top: 10, bottom: 4 }}
            slotProps={{
              legend: { hidden: !showLegend, direction: "row", position: { vertical: "bottom", horizontal: "middle" } } as any,
            }}
            data-testid="league-pie-arc"
            sx={{ width: '100%' }}
          />
          {showBottomPercent && (
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: half ? height * percentYOffset : height * 0.5,
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {`${decided > 0 ? Math.round((c / decided) * 100) : 0}%`}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default LeaguePieArc;
